/**
 * WidgetExportMenu — small dropdown menu in the widget header with three
 * actions: Export PNG, Copy to Clipboard (image), Export CSV.
 *
 * Used every brief, every status update — these are the cheapest UX wins
 * in the v3 plan. Phase J of issue #90.
 */
import { useEffect, useRef, useState } from 'react'
import { Download, Image as ImageIcon, Clipboard, Check, FileText } from 'lucide-react'
import {
  exportWidgetPng,
  copyWidgetToClipboard,
  exportRowsAsCsv,
} from '../../services/widgetExport'

interface Props {
  /** Title used for the downloaded filename. */
  title: string
  /** Card root element used to find the rendered ECharts instance. */
  cardRef: React.RefObject<HTMLElement>
  /** Optional rows for CSV export. If absent, the CSV item is hidden. */
  csvRows?: Array<Record<string, unknown>>
}

export default function WidgetExportMenu({ title, cardRef, csvRows }: Props) {
  const [open, setOpen] = useState(false)
  const [copyState, setCopyState] = useState<'idle' | 'ok' | 'err'>('idle')
  const menuRef = useRef<HTMLDivElement>(null)

  // Close menu on outside click.
  useEffect(() => {
    if (!open) return
    function handler(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const handlePng = () => {
    if (cardRef.current) exportWidgetPng(cardRef.current, title)
    setOpen(false)
  }

  const handleCopy = async () => {
    if (!cardRef.current) return
    const ok = await copyWidgetToClipboard(cardRef.current)
    setCopyState(ok ? 'ok' : 'err')
    setTimeout(() => setCopyState('idle'), 1500)
    setOpen(false)
  }

  const handleCsv = () => {
    if (csvRows && csvRows.length > 0) exportRowsAsCsv(csvRows, title)
    setOpen(false)
  }

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        title="Export"
        aria-label="Export widget"
        className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600"
      >
        <Download size={13} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 min-w-[180px] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg py-1 text-xs">
          <button
            onClick={handlePng}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
          >
            <ImageIcon size={12} />
            Export PNG
          </button>
          <button
            onClick={handleCopy}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
          >
            {copyState === 'ok'
              ? <Check size={12} className="text-green-500" />
              : <Clipboard size={12} />}
            {copyState === 'ok' ? 'Copied!' : copyState === 'err' ? 'Copy failed' : 'Copy as image'}
          </button>
          {csvRows && csvRows.length > 0 && (
            <button
              onClick={handleCsv}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
            >
              <FileText size={12} />
              Export CSV ({csvRows.length} rows)
            </button>
          )}
        </div>
      )}
    </div>
  )
}
